/**
 * Sección "Zona de peligro" — eliminar cuenta (soft delete con grace period).
 */

import { useState } from 'react'
import { toast } from 'sonner'
import { AlertTriangle, Trash2 } from 'lucide-react'

import {
  ActionButton,
  FieldRow,
  InlineAlert,
  SettingsSection,
  TextInput,
} from '@/components/ui/settings-primitives'
import { useDeleteAccount } from '@/hooks/api/use-profile'
import { setAccessToken } from '@/lib/api'
import { ROUTES } from '@/lib/constants'
import type { ProfileResponse } from '@/lib/api'
import { useNavigate } from 'react-router-dom'

const CONFIRMATION_PHRASE = 'ELIMINAR MI CUENTA'

export function DangerZoneSection({ profile }: { profile: ProfileResponse }) {
  const [show, setShow] = useState(false)
  const [password, setPassword] = useState('')
  const [totp, setTotp] = useState('')
  const [reason, setReason] = useState('')
  const [confirmation, setConfirmation] = useState('')

  const del = useDeleteAccount()
  const navigate = useNavigate()

  const phraseOk = confirmation.trim().toUpperCase() === CONFIRMATION_PHRASE

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      const resp = await del.mutateAsync({
        password,
        totp_code: profile.totp_enabled ? totp : null,
        confirmation,
        reason: reason || null,
      })
      toast.success(resp.message, { duration: 8000 })
      setAccessToken(null)
      navigate(ROUTES.LOGIN, { replace: true })
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.error(typeof detail === 'string' ? detail : 'No pudimos eliminar la cuenta')
    }
  }

  return (
    <SettingsSection
      title="Eliminar cuenta"
      description="Cerraremos todas tus sesiones y desactivaremos la cuenta inmediatamente. Tienes 30 días para contactar soporte si quieres revertir."
      footer={
        !show ? (
          <ActionButton variant="danger" onClick={() => setShow(true)}>
            <AlertTriangle className="h-4 w-4" />
            Eliminar mi cuenta
          </ActionButton>
        ) : null
      }
    >
      <InlineAlert variant="error">
        Esta acción es destructiva. Después del periodo de gracia se eliminarán
        permanentemente tu perfil, sesiones, conexiones OAuth, códigos MFA y eventos de
        auditoría asociados a tu identidad.
      </InlineAlert>

      {show && (
        <form onSubmit={onSubmit} className="mt-5 space-y-4">
          <FieldRow label="Contraseña actual">
            <TextInput
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required={profile.has_password}
              disabled={!profile.has_password}
            />
            {!profile.has_password && (
              <p className="mt-1 text-xs text-fg-muted">
                Tu cuenta no tiene contraseña. Configura una desde "Contraseña" antes de eliminarla.
              </p>
            )}
          </FieldRow>
          {profile.totp_enabled && (
            <FieldRow label="Código TOTP de 6 dígitos">
              <TextInput
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={totp}
                onChange={(e) => setTotp(e.target.value)}
                required
              />
            </FieldRow>
          )}
          <FieldRow label="Motivo (opcional)" hint="Nos ayuda a mejorar.">
            <TextInput
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={500}
              placeholder="¿Por qué te vas?"
            />
          </FieldRow>
          <FieldRow
            label="Escribe la frase para confirmar"
            hint={`Debe coincidir exactamente con "${CONFIRMATION_PHRASE}".`}
          >
            <TextInput
              value={confirmation}
              onChange={(e) => setConfirmation(e.target.value)}
              autoComplete="off"
              spellCheck={false}
              required
            />
          </FieldRow>
          <div className="flex gap-2">
            <ActionButton variant="ghost" onClick={() => setShow(false)} disabled={del.isPending}>
              Cancelar
            </ActionButton>
            <ActionButton
              type="submit"
              variant="danger"
              loading={del.isPending}
              disabled={!profile.has_password || !phraseOk}
            >
              <Trash2 className="h-4 w-4" />
              Eliminar permanentemente
            </ActionButton>
          </div>
        </form>
      )}
    </SettingsSection>
  )
}
